import React, { useEffect, useState } from "react";

/** Shared classes so every analytics card and table reads as one surface. */
export const CARD =
  "rounded-2xl shadow-lg border bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50 transition-colors duration-300";
export const CARD_PAD = `${CARD} p-5`;
export const TH =
  "px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap";
export const TH_NUM = `${TH} text-right`;
export const TD = "px-3 py-2 text-sm text-slate-700 dark:text-slate-200 whitespace-nowrap";
export const TD_NUM = `${TD} text-right tabular-nums`;
export const TR = "hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors";
export const SELECT =
  "h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";
export const LINK_BTN =
  "text-xs font-medium text-primary hover:underline dark:text-primaryLight";

/** Validated categorical palette (slots 1–8), light and dark steps. */
export const SERIES_LIGHT = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
export const SERIES_DARK = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"];

export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () => (document.documentElement.getAttribute("data-theme") || localStorage.getItem("theme")) === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

export function seriesColors(isDark: boolean): string[] {
  return isDark ? SERIES_DARK : SERIES_LIGHT;
}

/** sx for @mui/x-charts so ticks, legend and grid follow the page theme. */
export function chartSx(isDark: boolean) {
  return {
    width: "100%",
    "& .MuiChartsAxis-tickLabel": { fill: isDark ? "#94a3b8" : "#64748b", fontSize: "11px" },
    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: isDark ? "#334155" : "#e2e8f0" },
    "& .MuiChartsLegend-label": { fill: isDark ? "#cbd5e1" : "#475569", fontSize: "12px" },
    "& .MuiChartsGrid-line": { stroke: isDark ? "#1e293b" : "#f1f5f9" },
  } as const;
}

export function SectionTitle({ children, hint, action }: { children: React.ReactNode; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{children}</h3>
        {hint && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

export function ChartCard({ title, hint, children, action }: { title: string; hint?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className={CARD_PAD}>
      <SectionTitle hint={hint} action={action}>
        {title}
      </SectionTitle>
      {children}
    </div>
  );
}

export function TableCard({ title, hint, children, footer, action }: { title: string; hint?: string; children: React.ReactNode; footer?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="px-5 pt-5">
        <SectionTitle hint={hint} action={action}>
          {title}
        </SectionTitle>
      </div>
      <div className="overflow-x-auto">{children}</div>
      {footer && <div className="border-t border-slate-100 dark:border-slate-700">{footer}</div>}
    </div>
  );
}

export function EmptyRow({ colSpan, children = "Nothing to show for this period" }: { colSpan: number; children?: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-8 text-center text-xs text-slate-400 dark:text-slate-500">
        {children}
      </td>
    </tr>
  );
}

/** "Chrome" line under a value, e.g. the as-of day of a snapshot figure. */
export function Caption({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">{children}</p>;
}
